import { createContext, useContext, useState, useCallback } from "react";

const ModalContext = createContext(null);

export function ModalProvider({ children }) {
  const [modal, setModal] = useState(null);

  const showAlert = useCallback((message, type = "info") => {
    return new Promise((resolve) => {
      setModal({ mode: "alert", message, type, resolve });
    });
  }, []);

  const showConfirm = useCallback((message) => {
    return new Promise((resolve) => {
      setModal({ mode: "confirm", message, resolve });
    });
  }, []);

  const showPrompt = useCallback((message, defaultValue = "") => {
    return new Promise((resolve) => {
      setModal({ mode: "prompt", message, defaultValue, resolve });
    });
  }, []);

  function close(value) {
    if (modal?.resolve) modal.resolve(value);
    setModal(null);
  }

  return (
    <ModalContext.Provider value={{ showAlert, showConfirm, showPrompt }}>
      {children}
      {modal && <GlobalModal modal={modal} onClose={close} />}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    // fallback: context 없으면 브라우저 기본 사용
    return {
      showAlert: (msg) => { alert(msg); return Promise.resolve(); },
      showConfirm: (msg) => Promise.resolve(window.confirm(msg)),
      showPrompt: (msg, def) => Promise.resolve(window.prompt(msg, def)),
    };
  }
  return ctx;
}

function GlobalModal({ modal, onClose }) {
  const [inputValue, setInputValue] = useState(modal.defaultValue || "");

  const icon = modal.type === "success" ? "✓"
    : modal.type === "error" ? "✕"
    : modal.type === "warning" ? "!"
    : "ℹ";
  const color = modal.type === "success" ? "#10b981"
    : modal.type === "error" ? "#ef4444"
    : modal.type === "warning" ? "#f59e0b"
    : "#4fc3f7";

  return (
    <div className="global-modal-overlay" onClick={() => {
      if (modal.mode === "alert") onClose();
      else if (modal.mode === "confirm") onClose(false);
      else if (modal.mode === "prompt") onClose(null);
    }}>
      <div className="global-modal" onClick={(e) => e.stopPropagation()}>
        {modal.mode === "alert" && (
          <>
            <div className="global-modal-icon" style={{ background: color }}>{icon}</div>
            <div className="global-modal-message">{modal.message}</div>
            <button className="global-modal-btn" style={{ background: color }} onClick={() => onClose()}>
              확인
            </button>
          </>
        )}

        {modal.mode === "confirm" && (
          <>
            <div className="global-modal-icon" style={{ background: "#4fc3f7" }}>?</div>
            <div className="global-modal-message">{modal.message}</div>
            <div className="global-modal-actions">
              <button className="global-modal-cancel" onClick={() => onClose(false)}>취소</button>
              <button className="global-modal-confirm" onClick={() => onClose(true)}>확인</button>
            </div>
          </>
        )}

        {modal.mode === "prompt" && (
          <>
            <div className="global-modal-message">{modal.message}</div>
            <input
              className="global-modal-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") onClose(inputValue); }}
            />
            <div className="global-modal-actions">
              <button className="global-modal-cancel" onClick={() => onClose(null)}>취소</button>
              <button className="global-modal-confirm" onClick={() => onClose(inputValue)}>확인</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
